<template>
  <div 
    v-if="element.type === 'locked' && element.locked"
    class="max-w-xs mx-auto pt-8 pb-4"
  >
    <form class="space-y-2">
      <div class="text-center">
        <aw-icon
          icon="lock"
          class="w-12 h-12 text-bc-blue "
        />
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-1 gap-x-4 sm:grid-cols-6" v-show="incorrectPwd">
        <div class="sm:col-span-6">
          <div class="mt-1 flex items-center text-xs text-red-600">
            {{$t('Incorrect password')}}
          </div>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <div class="mt-1 flex flex-wrap">
            <input
              type="password"
              class="form-control rounded-md shadow-sm text-center"
              :placeholder="$t('Enter password')"
              v-model="pwd"
            />
          </div>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <div class="mt-1 flex">
            <button class="btn btn-primary block w-full" @click.stop.prevent="unlock">{{$t('Unlock')}}</button>
          </div>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-1 gap-x-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <div class="mt-1 flex items-center text-xs">
            {{$t('Content in this page is locked. Enter password to unlock.')}}
          </div>
        </div>
      </div>
    </form>
  </div>
  <div 
    v-else-if="element.type === 'locked' && !element.locked"
    class="max-w-xs mx-auto pt-8 pb-4"
  >
    <form class="space-y-2">
      <div class="text-center">
        <aw-icon
          icon="lock"
          class="w-12 h-12 text-bc-blue "
        />
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-1 gap-x-4 sm:grid-cols-6" v-show="error">
        <div class="sm:col-span-6">
          <div class="mt-1 flex items-center text-xs text-red-600" v-show="error === 1">
            {{$t('Please enter password to lock page')}}
          </div>
          <div class="mt-1 flex items-center text-xs text-red-600" v-show="error === 2">
            {{$t('Password is not the same')}}
          </div>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <div class="mt-1 flex flex-wrap">
            <input
              type="password"
              class="form-control rounded-md shadow-sm text-center"
              :placeholder="$t('Enter password')"
              v-model="pwd"
            />
          </div>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <div class="mt-1 flex flex-wrap">
            <input
              type="password"
              class="form-control rounded-md shadow-sm text-center"
              :placeholder="$t('Enter password again')"
              v-model="pwd1"
            />
          </div>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <div class="mt-1 flex">
            <button class="btn block w-full" :class="{ 'btn-primary': error === null }" @click.stop.prevent="lock">{{ $t('Lock') }}</button>
            <button class="btn block w-full" @click.stop.prevent="cancel">{{ $t('Cancel') }}</button>
          </div>
        </div>
      </div>
      <div class="mt-6 grid grid-cols-1 gap-y-1 gap-x-4 sm:grid-cols-6">
        <div class="sm:col-span-6">
          <div class="mt-1 flex items-center text-xs">
            {{$t('Enter password to lock page.')}}
          </div>
        </div>
      </div>
    </form>
  </div>
</template>

<script>
import { mapStores } from 'pinia'
import Lock from '../../mixins/Lock'
import assign from 'lodash/assign'
import { DocumentApi } from '../../test-api/index.ts'
import useBcSelectedStore from '@/frontapp/store/bcSelected'
import DocumentStore from '@/frontapp/mixins/DocumentStore'
import UpdateDocumentRevision from '@/frontapp/mixins/actions/UpdateDocumentRevision'

const documentApi = new DocumentApi({}, '/api/v2')

export default {
  mixins: [
    DocumentStore,
    Lock,
  ],
  props: {
    element: {
      type: Object,
      required: true,
    },
  },
  computed: {
    ...mapStores(useBcSelectedStore),
  },
  methods: {
    lock() {
      if (this.error !== null) {
        return
      }

      const pwd = this.pwd

      this.bcDocumentStore.addToUpdateQueue(() => new Promise((resolve/*, reject*/) => {
        // eslint-disable-next-line no-extra-semi
        ;(async () => {
          const data = await documentApi.elementLock(
            this.bcDocumentStore.document.id,
            this.bcDocumentStore.document.bc_document_rev_id,
            this.element.id,
            { pwd }
          )

          if (this.element.oldType) {
            delete this.element.oldType
          }

          if (data.data.bc_document_rev_id !== this.bcDocumentStore.documentRev) {
            await UpdateDocumentRevision(this.bcDocumentStore, data.data.bc_document_rev_id, this.$router)
          
          } else {
            this.bcDocumentStore.removeFromFlatten(this.element)
            assign(this.element, data.data)
            this.bcDocumentStore.addToFlatten(this.element)
            this.bcSelectedStore.resetSelected()
          }
          
          resolve()
        })()
      }))
    },
    cancel () {
      const pageEl = this.bcDocumentStore.findElementInDocument(this.element.id)
      pageEl.type = this.element.oldType
      delete pageEl.oldType
    },
    async unlock() {
      let elementUnlockResponse
      try {
        if (this.$route.matched[0].name === 'BCSharedDocument') {
          elementUnlockResponse = await documentApi.elementUnlockShared(
            this.bcDocumentStore.document.id,
            this.element.id,
            { pwd: this.pwd }
          )
        } else if (this.bcDocumentStore.documentRev) {
          elementUnlockResponse = await documentApi.elementUnlockRev(
            this.bcDocumentStore.document.id,
            this.bcDocumentStore.document.bc_document_rev_id,
            this.element.id,
            { pwd: this.pwd }
          )        
        } else {
          elementUnlockResponse = await documentApi.elementUnlock(
            this.bcDocumentStore.document.id,
            this.element.id,
            { pwd: this.pwd }
          )
        }

        assign(this.element, elementUnlockResponse.data)
        this.bcDocumentStore.addToFlatten(this.element)
        this.bcSelectedStore.resetSelected()
        this.incorrectPwd = false
      } catch (e) {
        this.incorrectPwd = true
      }
    }
  }
}
</script>

<style lang="scss" scoped></style>
